import React, { FC, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Store } from '../../redux';
import { AlertSliceState, triggerAlert } from '../../redux/reducers';
import { Box, styled } from '@mui/material';
import { CheckCircle2, AlertTriangle, AlertOctagon, X } from 'lucide-react';

const ISO_PADDING = 'env(safe-area-inset-bottom)';

// Per-severity styling: icon, accent color, glow tint. Each value
// references the Nord palette tokens the rest of the chat surface uses
// so the toast feels native to the app rather than a stock MUI snackbar.
const TONE: Record<
  NonNullable<AlertSliceState['type']>,
  {
    Icon: typeof CheckCircle2;
    accent: string;       // border-left + icon
    tint: string;         // soft fill behind the icon
    label: string;        // a11y label
  }
> = {
  success: {
    Icon: CheckCircle2,
    accent: 'var(--nord-14)',
    tint: 'rgba(138, 217, 160, 0.18)',
    label: 'Success',
  },
  warning: {
    Icon: AlertTriangle,
    accent: 'var(--nord-13)',
    tint: 'rgba(245, 165, 36, 0.20)',
    label: 'Warning',
  },
  error: {
    Icon: AlertOctagon,
    accent: 'var(--nord-11)',
    tint: 'rgba(194, 47, 55, 0.18)',
    label: 'Error',
  },
};

export const AlertProvider: FC = () => {
  const { showAlert, type, message } = useSelector(
    (state: Store) => state.alertReducer
  );
  const dispatch = useDispatch();

  const dismiss = useCallback(() => {
    dispatch(
      triggerAlert({ showAlert: false, type: null, message: '' })
    );
  }, [dispatch]);

  // Auto-dismiss after 5s. Resets if the same toast updates.
  useEffect(() => {
    if (!showAlert) return;
    const timer = setTimeout(dismiss, 5000);
    return () => clearTimeout(timer);
  }, [dispatch, showAlert, message, type, dismiss]);

  if (!showAlert || !type) return null;
  const tone = TONE[type];
  const Icon = tone.Icon;

  return (
    <AlertWrapper>
      <ToastCard
        role="status"
        aria-live="polite"
        aria-label={tone.label}
        sx={{
          // Per-tone accent via a thin left rail.
          borderLeft: `3px solid ${tone.accent}`,
        }}
      >
        <IconBubble sx={{ backgroundColor: tone.tint, color: tone.accent }}>
          <Icon size={18} strokeWidth={2.25} />
        </IconBubble>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={theme => ({
              fontSize: '0.78rem',
              fontWeight: 700,
              letterSpacing: '0.4px',
              textTransform: 'uppercase',
              color: tone.accent,
              marginBottom: theme.spacing(0.25),
              lineHeight: 1.1,
            })}
          >
            {tone.label}
          </Box>
          <Box
            sx={theme => ({
              fontSize: '0.875rem',
              color: theme.palette.text.primary,
              lineHeight: 1.4,
              wordBreak: 'break-word',
            })}
          >
            {message}
          </Box>
        </Box>

        <DismissButton
          type="button"
          aria-label="Dismiss notification"
          onClick={dismiss}
        >
          <X size={14} strokeWidth={2.5} />
        </DismissButton>
      </ToastCard>
    </AlertWrapper>
  );
};

export const AlertWrapper = styled(Box)(({ theme }) => ({
  position: 'fixed',
  right: theme.spacing(2),
  bottom: `calc(${theme.spacing(2)} + ${ISO_PADDING})`,
  zIndex: 1500,
  width: 380,
  maxWidth: 'calc(100vw - 16px)',
  pointerEvents: 'none',
  '& > *': { pointerEvents: 'auto' },
  [theme.breakpoints.down('sm')]: {
    width: 'auto',
    right: theme.spacing(1),
    left: theme.spacing(1),
    bottom: `calc(${theme.spacing(1)} + ${ISO_PADDING})`,
  },
}));

const ToastCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  gap: theme.spacing(1.25),
  padding: theme.spacing(1.5, 1.75),
  borderRadius: theme.spacing(1.5),
  backgroundColor:
    theme.palette.mode === 'dark'
      ? 'rgba(17, 33, 27, 0.92)' // night-table surface
      : 'rgba(255, 250, 240, 0.96)', // ivory paper
  backdropFilter: 'blur(14px) saturate(140%)',
  WebkitBackdropFilter: 'blur(14px) saturate(140%)',
  border:
    theme.palette.mode === 'dark'
      ? '1px solid rgba(247, 240, 225, 0.14)'
      : '1px solid rgba(20, 35, 29, 0.12)',
  boxShadow:
    theme.palette.mode === 'dark'
      ? '0 12px 32px rgba(0, 0, 0, 0.45), 0 1px 2px rgba(0, 0, 0, 0.25)'
      : '0 12px 32px rgba(60, 40, 10, 0.16), 0 1px 2px rgba(60, 40, 10, 0.08)',
  animation: 'bhabi-toast-in 220ms cubic-bezier(0.16, 1, 0.3, 1)',
  '@keyframes bhabi-toast-in': {
    from: {
      opacity: 0,
      transform: 'translateY(12px) scale(0.97)',
    },
    to: {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
  },
}));

const IconBubble = styled(Box)(() => ({
  flexShrink: 0,
  width: 32,
  height: 32,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const DismissButton = styled('button')(({ theme }) => ({
  flexShrink: 0,
  width: 24,
  height: 24,
  marginTop: 2,
  borderRadius: 6,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
  transition: 'background-color 140ms ease, color 140ms ease',
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? 'rgba(247, 240, 225, 0.08)'
        : 'rgba(20, 35, 29, 0.06)',
    color: theme.palette.text.primary,
  },
  '&:focus-visible': {
    outline: `2px solid var(--nord-8)`,
    outlineOffset: 1,
  },
}));

export const useAlert = () => {
  const dispatch = useDispatch();

  const addAlert = useCallback(
    ({ type, message }: Omit<AlertSliceState, 'showAlert'>) => {
      dispatch(triggerAlert({ showAlert: true, type, message }));
    },
    [dispatch]
  );
  return { addAlert };
};
